import * as yup from 'yup'
import { useFormik } from 'formik'
import { useState } from 'react'
import commonCodebase from 'common-recruspin-codebase'
import axiosInstance from '../../../../../utils/axiosInstance'
import { setTriggerOrdersRefetch } from '../../../../../store/slices/general'
import { makeMultipartFormData } from '../../../../../utils/tools'
import { useDispatch } from '../../../../../store/store'
import { handleFileDeletions, setFormikFileFields } from '../../../ReadEditOrder/components/funcs'
import { TunsolvedAny } from '../../../../../types/unsolved'
import { Campaign, Job } from './types'

const { statuses } = commonCodebase.orders

interface UseEditOrderFormParams {
  order: any
  assignees: { id: string; firstName: string; lastName: string }[]
  customers: { id: string; name: string; emails: null | string[] }[]
  originalMetaCampaigns: { id: string; name: string }[] | undefined
  tiktokCampaigns: { id: string; name: string }[] | undefined
  googleCampaigns: { id: string; name: string }[] | undefined
  jobs: Job[] | undefined
  isCustomer: boolean | string | undefined
  headerImageId: string | null
  previewFiles: any[]
  assetsFiles: any[]
  videoAssets: any[]
  previewIdsToDelete: string[]
  assetsIdsToDelete: string[]
  videoAssetsIdsToDelete: string[]
  jobFile: File | null
  existingJobFile: any
  deletePreviewImage: (id: string) => Promise<void>
  deletePreviewAudio: (id: string) => Promise<void>
  deletePreviewVideo: (id: string) => Promise<void>
  deleteAssetImage: (id: string) => Promise<void>
  deleteFileAssetFile: (id: string) => Promise<void>
  onSave?: () => void
}

export function useEditOrderForm({
  order,
  assignees,
  customers,
  originalMetaCampaigns,
  tiktokCampaigns,
  googleCampaigns,
  jobs,
  isCustomer,
  headerImageId,
  previewFiles,
  assetsFiles,
  videoAssets,
  previewIdsToDelete,
  assetsIdsToDelete,
  videoAssetsIdsToDelete,
  jobFile,
  existingJobFile,
  deletePreviewImage,
  deletePreviewAudio,
  deletePreviewVideo,
  deleteAssetImage,
  deleteFileAssetFile,
  onSave,
}: UseEditOrderFormParams) {
  const dispatch = useDispatch()
  const [loadingMutation, setLoadingMutation] = useState<boolean>(false)
  const [mutationError, setMutationError] = useState<[string] | null>(null)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)

  // The dropdown option lists (customers, assignees, campaigns, jobs) load
  // asynchronously and start as a single blank placeholder ({ id: '' }). Until
  // the real data arrives, `oneOf` would reject every value — including the
  // order's real, already-selected one. Only apply the `oneOf` constraint once
  // the list holds at least one real (non-empty) id, so validation on save
  // doesn't spuriously fail before the options have loaded.
  const realIds = (options: (string | undefined)[]) =>
    options.filter((id): id is string => Boolean(id))

  const oneOfWhenLoaded = (schema: yup.StringSchema, options: (string | undefined)[]) => {
    const ids = realIds(options)
    return ids.length ? schema.oneOf(ids) : schema
  }

  const assigneeIds = realIds(assignees.map((i) => i?.id))

  // Customers may leave these empty so they can save an order without filling in
  // fields that do not concern them (e.g. self-service orders, which are created
  // without type, price or runtime). Empty values are not sent on submit, so the
  // stored value is kept. Internal users keep the strict rules.
  // The form holds '' for an empty number, which yup casts to NaN and rejects even
  // when optional — hence the transform.
  const optionalNumber = () =>
    yup
      .number()
      .min(0)
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
  const placeMinMessage = 'Place should be of minimum 2 characters length'

  const validationSchema = yup.object({
    customer: oneOfWhenLoaded(
      yup.string().required(),
      customers.map((i) => i?.id)
    ),
    taskType: isCustomer ? yup.string().nullable() : yup.string().required(),
    place: isCustomer
      ? yup
          .string()
          .test('place-min-length', placeMinMessage, (value) => !value || value.trim().length >= 2)
      : yup.string().required().min(2, placeMinMessage),
    assignee: isCustomer
      ? yup.string().nullable()
      : (assigneeIds.length ? yup.string().oneOf([...assigneeIds, '']) : yup.string())
          .nullable()
          .default(null),
    jobName: yup.string().required().min(2, 'Job title should be of minimum 2 characters length'),
    status: yup
      .string()
      .required()
      .oneOf(statuses.map((i: { value: string }) => i.value))
      .default(''),
    channel: yup.array().required(),
    jobAdLink: yup
      .string()
      .nullable()
      .test('jobAdLink-or-jobFile', 'Either Job Ad Link or Job File is required', function () {
        return true
      }),
    applyUrl: yup.string().nullable(),
    keywords: yup.string().nullable(),
    adDescription: yup.string().nullable(),
    externalImportId: yup.string().nullable(),
    employmentType: yup.string().nullable(),
    metaCampaigns: yup.array().of(yup.string()).nullable(),
    originalMetaCampaign: oneOfWhenLoaded(
      yup.string(),
      originalMetaCampaigns ? originalMetaCampaigns.map((i) => i?.id) : []
    ).nullable(),
    tiktokCampaign: oneOfWhenLoaded(
      yup.string(),
      tiktokCampaigns ? tiktokCampaigns.map((i) => i?.id) : []
    ).nullable(),
    googleCampaigns: yup
      .array()
      .of(oneOfWhenLoaded(yup.string(), googleCampaigns ? googleCampaigns.map((i) => i?.id) : []))
      .nullable(),
    atsJob: oneOfWhenLoaded(yup.string(), jobs ? jobs.map((i) => i?.id) : []).nullable(),
    comments: yup.string().max(1000, 'Maximum 1000 Characters allowed'),
    budget: isCustomer ? optionalNumber() : yup.number().min(0).required(),
    price: isCustomer ? optionalNumber() : yup.number().min(0).required(),
    runtime: isCustomer ? optionalNumber() : yup.number().min(0).required(),
    previews: yup.object().nullable(),
    imageAssets: yup.object().nullable(),
    videoAssets: yup.object().nullable(),
  })

  const formik = useFormik({
    initialValues: {
      customer: order?.customer?.id || customers[0]?.id || '',
      assignee: order?.assignee?.id || assignees[0]?.id || '',
      jobName: order?.jobName || '',
      status: order?.status || '',
      place: order?.place || '',
      channel: order?.channel || [],
      taskType: order?.jobType || '',
      jobAdLink: order?.jobAdLink || '',
      applyUrl: order?.applyUrl || '',
      keywords: order?.keywords || '',
      adDescription: order?.adDescription || '',
      externalImportId: order?.externalImportId || '',
      employmentType: order?.employmentType || '',
      metaCampaigns: order?.metaCampaign?.map((c: Campaign) => c.id) || ([] as string[]),
      originalMetaCampaign: order?.originalMetaCampaign?.id || '',
      tiktokCampaign: order?.tiktokCampaign?.id || '',
      googleCampaigns: order?.googleCampaigns?.map((c: Campaign) => c.id) || ([] as string[]),
      atsJob: order?.jobs?.[0]?.id || '',
      comments: order?.comments || '',
      budget: order?.budget || '',
      price: order?.price || '',
      runtime: order?.runtime || '',
      previews: null,
      previewsVideo: null,
      previewsAudio: null,
      imageAssets: null,
      videoAssets: null,
      notifyUser: false,
      freeFeed: order?.freeFeed || false,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const isEmpty = (value: unknown) => value === '' || value === null || value === undefined

      const newFormData: TunsolvedAny = {
        jobName: values.jobName,
        // Left out when empty (only possible for customers), keeping the stored value:
        // Keystone rejects '' for these enum/number fields.
        jobType: isEmpty(values.taskType) ? undefined : values.taskType,
        place: values.place,
        channel: values.channel,
        budget: values.budget ? String(values.budget) : null,
        price: isEmpty(values.price) ? undefined : String(values.price),
        runtime: isEmpty(values.runtime) ? undefined : values.runtime,
        jobAdLink: values.jobAdLink,
        applyUrl: values.applyUrl,
        keywords: values.keywords,
        adDescription: values.adDescription,
        externalImportId: values.externalImportId,
        employmentType: values.employmentType,
        comments: values.comments,
        status: values.status,
        freeFeed: values.freeFeed,
        customer: {
          connect: { id: values.customer },
        },
        previews: values.previews,
        previewsAudio: values.previewsAudio,
        previewsVideo: values.previewsVideo,
        imageAssets: values.imageAssets,
        videoAssets: values.videoAssets,
      }

      if (values.assignee && values.assignee.trim() !== '') {
        newFormData.assignee = {
          connect: { id: values.assignee },
        }
      }

      const existingCampaignIds = order?.metaCampaign?.map((c: Campaign) => c.id) || []
      const newCampaignIds = values.metaCampaigns || []
      const toConnect = newCampaignIds.filter((id: string) => !existingCampaignIds.includes(id))
      const toDisconnect = existingCampaignIds.filter((id: string) => !newCampaignIds.includes(id))

      if (toConnect.length > 0 || toDisconnect.length > 0) {
        newFormData.metaCampaign = {}
        if (toConnect.length > 0) {
          newFormData.metaCampaign.connect = toConnect.map((id: string) => ({ id }))
        }
        if (toDisconnect.length > 0) {
          newFormData.metaCampaign.disconnect = toDisconnect.map((id: string) => ({ id }))
        }
      } else {
        newFormData.metaCampaign = undefined
      }

      if (values.originalMetaCampaign) {
        newFormData.originalMetaCampaign = { connect: { id: values.originalMetaCampaign } }
      } else if (order?.originalMetaCampaign && !values.originalMetaCampaign) {
        newFormData.originalMetaCampaign = { disconnect: true }
      } else {
        newFormData.originalMetaCampaign = undefined
      }

      if (values.tiktokCampaign) {
        newFormData.tiktokCampaign = { connect: { id: values.tiktokCampaign } }
      } else if (order?.tiktokCampaign && !values.tiktokCampaign) {
        newFormData.tiktokCampaign = { disconnect: true }
      } else {
        newFormData.tiktokCampaign = undefined
      }

      const existingGoogleIds: string[] = order?.googleCampaigns?.map((c: Campaign) => c.id) || []
      const newGoogleIds: string[] = values.googleCampaigns || []
      const googleToConnect = newGoogleIds.filter((id) => !existingGoogleIds.includes(id))
      const googleToDisconnect = existingGoogleIds.filter((id) => !newGoogleIds.includes(id))

      if (googleToConnect.length > 0 || googleToDisconnect.length > 0) {
        newFormData.googleCampaigns = {}
        if (googleToConnect.length > 0) {
          newFormData.googleCampaigns.connect = googleToConnect.map((id) => ({ id }))
        }
        if (googleToDisconnect.length > 0) {
          newFormData.googleCampaigns.disconnect = googleToDisconnect.map((id) => ({ id }))
        }
      }

      if (jobFile) {
        newFormData.jobFile = {
          create: {
            name: jobFile.name,
            file: { upload: jobFile },
          },
        }
      } else if (!existingJobFile && order?.jobFile) {
        newFormData.jobFile = { disconnect: true }
      }

      const mapFileFields = [
        { name: 'previews', source: 'image' },
        { name: 'previewsAudio', source: 'image' },
        { name: 'previewsVideo', source: 'image' },
        { name: 'imageAssets', source: 'image' },
        { name: 'videoAssets', source: 'file' },
        { name: 'jobFile', source: 'file' },
      ]

      const formData = makeMultipartFormData(mapFileFields, newFormData)

      formData.append('headerImageId', headerImageId || '')
      formData.append('atsJobId', values.atsJob || '')

      try {
        setLoadingMutation(true)
        await axiosInstance.post(`/orders/edit/${order.id}`, formData, {
          params: {
            notifyUser: values.notifyUser ? 1 : undefined,
          },
          headers: {
            'Content-Type': 'multipart/form-data;',
          },
        })
        setSuccessModalOpen(true)
        dispatch(setTriggerOrdersRefetch(true))
        onSave?.()
      } catch (error: any) {
        const serverMessage = error?.response?.data?.message
        setMutationError([serverMessage || error?.response?.statusText || 'Failed to save order'])
      } finally {
        setLoadingMutation(false)
      }
    },
  })

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!isCustomer && !formik.values.jobAdLink && !jobFile && !existingJobFile) {
      setFormikErrors(['Please provide either a Job Ad Link or upload a Job File'])
      setErrorsModalOpen(true)
      return
    }

    // Validate now: formik.errors is only filled after a field has changed, so on an
    // untouched form it was empty and an invalid save failed without any message.
    const errors = Object.values(await formik.validateForm())
    if (errors.length) {
      setErrorsModalOpen(true)
      setFormikErrors(errors as string[])
      return
    }

    await setFormikFileFields(previewFiles, formik, assetsFiles, videoAssets)
    setLoadingMutation(true)
    try {
      await handleFileDeletions(
        previewIdsToDelete,
        deletePreviewImage,
        deletePreviewAudio,
        deletePreviewVideo,
        assetsIdsToDelete,
        deleteAssetImage,
        videoAssetsIdsToDelete,
        deleteFileAssetFile,
        order
      )
      formik.handleSubmit()
    } catch (err: any) {
      setErrorsModalOpen(true)
      setFormikErrors([err?.message])
      setLoadingMutation(false)
    }
  }

  return {
    formik,
    handleSubmit,
    loadingMutation,
    mutationError,
    successModalOpen,
    setSuccessModalOpen,
    formikErrors,
    errorsModalOpen,
    setErrorsModalOpen,
  }
}
